import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { Box, List, Typography } from '@mui/material';
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import { TrackedListItemButton as ListItemButton } from '../../../components/TrackedComponents';
import palette from '../../ColorPalette';
import { RTLDirectionContext } from '../../Layout';
import { REPORT_TYPE_TO_URL_PARAM } from './ReportComponent';

export const REPORT_TYPES = {
    safety: ['SAFETY_EVENTS', 'DRIVER_SCORES', 'PENDING_SESSIONS', 'COMPLETED_SESSIONS'],
    insurance: ['ACCIDENTS_REPORT'],
    operations: ['GEOFENCE_EVENTS', 'MALFUNCTIONS', 'DEVICE_WELLNESS', 'INACTIVE_DEVICES', 'OPERATION_REPORT'],
};

const ALERT_TYPES = {
    safety: ['SAFETY_EVENTS'],
    insurance: ['ACCIDENTS_REPORT'],
    operations: ['GEOFENCE_EVENTS', 'DEVICE_WELLNESS'],
};

interface ReportMenuProps {
    type: 'REPORTS' | 'ALERTS';
    setSelectedReportType: (reportType: string | null) => void;
}

const ReportMenu: React.FC<ReportMenuProps> = (props: ReportMenuProps) => {
    const { t } = useTranslation();
    const isRTL = useContext(RTLDirectionContext);
    const history = useHistory();
    const isReports = props.type === 'REPORTS';
    const options = isReports ? REPORT_TYPES : ALERT_TYPES;

    return (
        <Box
            sx={{
                width: '100%',
                height: '100%',
                maxWidth: '700px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
            }}
        >
            <Typography variant="h5" sx={{ mb: 1 }}>
                {t(`content.reports.${props.type.toLocaleLowerCase()}`)}
            </Typography>
            {Object.entries(options).map(([category, reportsTypes]) => {
                return (
                    <Box
                        key={category}
                        sx={{
                            width: '100%',
                            display: 'flex',
                            justifyContent: 'space-between',
                            mb: 3,
                        }}
                    >
                        <Typography sx={{ fontSize: 18, fontWeight: 500 }}>
                            {t(`content.reports.categories.${category}`)}
                        </Typography>
                        <List disablePadding sx={{ flexGrow: 1, maxWidth: '450px' }}>
                            {reportsTypes.map((reportType, idx) => (
                                <ListItemButton
                                    key={reportType}
                                    id={`btn-${reportType.toLocaleLowerCase().replace('_', '-')}`}
                                    data-testid={`btn-${reportType.toLocaleLowerCase().replace('_', '-')}`}
                                    onClick={() => {
                                        history.push(
                                            `/${props.type.toLocaleLowerCase()}/${
                                                REPORT_TYPE_TO_URL_PARAM[reportType]
                                            }`,
                                        );
                                        props.setSelectedReportType(reportType);
                                    }}
                                    sx={{
                                        border: `1px solid ${palette.neutral[300]}`,
                                        borderBottom:
                                            idx === reportsTypes.length - 1 ? `1px solid ${palette.neutral[300]}` : 0,
                                        borderTopLeftRadius: idx === 0 ? '4px' : 0,
                                        borderTopRightRadius: idx === 0 ? '4px' : 0,
                                        borderBottomLeftRadius: idx === reportsTypes.length - 1 ? '4px' : 0,
                                        borderBottomRightRadius: idx === reportsTypes.length - 1 ? '4px' : 0,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            width: '100%',
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'flex-start',
                                                p: 0.5,
                                            }}
                                        >
                                            <Typography sx={{ fontWeight: 500 }}>
                                                {t(`content.reports.form.type.${reportType.toLocaleLowerCase()}`)}
                                            </Typography>
                                            <Typography fontSize={14}>
                                                {t(
                                                    `content.reports.${
                                                        isReports ? 'report' : 'alert'
                                                    }_descriptions.${reportType.toLocaleLowerCase()}`,
                                                )}
                                            </Typography>
                                        </Box>
                                        <ArrowForwardIosIcon
                                            sx={{ fontSize: 16, transform: isRTL ? 'rotate(180deg)' : undefined }}
                                        />
                                    </Box>
                                </ListItemButton>
                            ))}
                        </List>
                    </Box>
                );
            })}
        </Box>
    );
};

export default ReportMenu;
